/**
 * Hook para verificar duplicidade de telefone antes do cadastro de contato
 * 
 * Utiliza normalização de telefone brasileiro para garantir que contatos
 * com variações do mesmo número (com ou sem 9º dígito, com ou sem DDI)
 * sejam detectados como duplicados.
 * 
 * @example
 * const { checkPhoneDuplicate } = usePhoneDuplicateCheck();
 * const result = await checkPhoneDuplicate(tenantId, '62999325228');
 * if (result.isDuplicate) {
 *   toast.error(`Este telefone já pertence a "${result.existingContact?.name}"`);
 * }
 */

import { supabase } from '@/integrations/supabase/client';
import { normalizePhoneIdentifier } from '@/lib/phone-utils';

/**
 * Resultado da verificação de duplicidade de telefone
 */
export interface PhoneDuplicateCheckResult {
  /** Indica se foi encontrado um contato com este telefone */
  isDuplicate: boolean;
  /** Dados do contato existente (se encontrado) */
  existingContact: {
    id: string;
    name: string | null;
    phone: string | null;
  } | null;
}

/**
 * Hook para verificar se um telefone já existe na base de contatos do tenant
 * 
 * Aplica normalização brasileira antes da busca para evitar falsos negativos
 * causados por diferenças de formatação (DDI, 9º dígito, etc.)
 */
export function usePhoneDuplicateCheck() {
  /**
   * Verifica se um telefone já está cadastrado para outro contato no tenant
   * 
   * @param tenantId - ID do tenant para buscar contatos
   * @param phone - Telefone a verificar (qualquer formato)
   * @param excludeContactId - ID do contato a excluir da busca (para edição)
   * @returns Resultado indicando se é duplicado e dados do contato existente
   */
  const checkPhoneDuplicate = async (
    tenantId: string,
    phone: string,
    excludeContactId?: string
  ): Promise<PhoneDuplicateCheckResult> => {
    // Retorno padrão para telefone vazio ou inválido
    const emptyResult: PhoneDuplicateCheckResult = {
      isDuplicate: false,
      existingContact: null,
    };

    // Normaliza o telefone usando a função existente
    const normalizedPhone = normalizePhoneIdentifier(phone);
    
    // Telefone muito curto não é válido para verificação
    if (!normalizedPhone || normalizedPhone.length < 10) {
      return emptyResult;
    }
    
    try {
      // Monta query base filtrando por tenant e telefone normalizado
      let query = supabase
        .from('contacts')
        .select('id, name, phone')
        .eq('tenant_id', tenantId)
        .eq('phone', normalizedPhone);
      
      // Exclui o próprio contato se estiver editando
      if (excludeContactId) {
        query = query.neq('id', excludeContactId);
      }
      
      const { data, error } = await query.maybeSingle();
      
      if (error) {
        console.error('[usePhoneDuplicateCheck] Erro ao verificar duplicidade:', error);
        // Em caso de erro, permitimos continuar para não bloquear o usuário
        return emptyResult;
      }
      
      return {
        isDuplicate: !!data,
        existingContact: data || null,
      };
    } catch (error) {
      console.error('[usePhoneDuplicateCheck] Erro inesperado:', error);
      return emptyResult;
    }
  };
  
  return { checkPhoneDuplicate };
}
